import React from 'react'
import { Badge, ListItemText, Icon } from '@mui/material'
import { useTranslation } from 'react-i18next'

import Header from 'components/Shared/Header'
import ListItemIcon from 'components/Sidebar/Styled/ListItemIcon'
import variables from 'assets/styles/scss/shared/_variables.module.scss'

type CustomBadgeProps = {
  title?: string
  badgeContent?: string
  contentType: string
  iconName?: string
  badgeType: string
}

enum badgeContentType {
  Beta = 0,
  New = 1,
  Expired = 2,
}

const badgeData = [
  { name: 'beta', bgColour: variables['badge-beta'] },
  { name: 'new', bgColour: variables['badge-new'] },
  { name: 'expired', bgColour: variables['badge-expired'] },
]

const badgeStyle = (badgeColor: string) => ({
  '& .MuiBadge-badge': {
    backgroundColor: badgeColor,
    color: variables.white,
  },
})

const getHeaderBadge = (title: string, badgeType: string) => {
  const { t } = useTranslation()
  const option = badgeContentType[badgeType as keyof typeof badgeContentType]
  return (
    !!title && (
      <Badge
        badgeContent={t(`common.releaseLabel.${badgeData[option].name}`)}
        sx={badgeStyle(badgeData[option].bgColour)}
        data-testid="pageHeader-badge"
      >
        <Header title={title} />
      </Badge>
    )
  )
}

const getSidebarMenuItemBadge = (
  badgeContent: string,
  iconName: string,
  badgeType: string,
) => {
  const option = badgeContentType[badgeType as keyof typeof badgeContentType]
  return (
    !!iconName && (
      <Badge
        badgeContent={badgeContent}
        variant={!badgeContent ? 'dot' : 'standard'}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={badgeStyle(badgeData[option].bgColour)}
        data-testid="sidebar-menuItem-badge"
      >
        <ListItemIcon>
          <Icon className="material-icons-outlined">{iconName}</Icon>
        </ListItemIcon>
      </Badge>
    )
  )
}

const getSidebarSubMenuItemBadge = (title: string, badgeType: string) => {
  const option = badgeContentType[badgeType as keyof typeof badgeContentType]
  return (
    !!title && (
      <Badge
        variant="dot"
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={[badgeStyle(badgeData[option].bgColour)]}
        data-testid="sidebar-subMenuItem-badge"
      >
        <ListItemText primary={title} />
      </Badge>
    )
  )
}

const CustomBadge = ({
  title,
  badgeContent,
  contentType,
  iconName,
  badgeType,
}: CustomBadgeProps) => {
  const hash: Record<string, any> = {
    Header: () => getHeaderBadge(title ? title : '', badgeType),
    MenuItem: () =>
      getSidebarMenuItemBadge(badgeContent || '', iconName || '', badgeType),
    SubMenuItem: () =>
      getSidebarSubMenuItemBadge(title ? title : '', badgeType),
  }
  return (contentType && hash[contentType] && hash[contentType]()) || null
}

export default CustomBadge
